import { MaintenanceWindowFormPatchSpecificsView } from '../view/MaintenanceWindowFormPatchSpecificsView';
import { templateMaintenanceWindowFormPatchSpecifics } from '../view/template';
import { BaseWebComponentController } from '../../../framework/BaseWebComponentController';
import { MaintenanceWindowFormPatchSpecificsWebComponent } from '..';
import {
  AdditionalPatchSettingsEnablingConfig,
  ShorthandPatchScopeField,
  FormEnablingConfig,
  MaintenanceWindowPatchSpecificsFormData,
  SchedulingOptionsEnablingConfig,
  TargetScopeEnablingConfig,
} from '../types';
import { ToggleEvents } from '../../../ui/gzToggle/types';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { GzDialog, GzDialogEvents } from '../../../ui/gzDialog/component/GzDialog';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { EVENTS } from '../../../maintenanceWindows/relayPatchCachingServers/types';

export class MaintenanceWindowFormPatchSpecificsController extends BaseWebComponentController {
  public component: MaintenanceWindowFormPatchSpecificsWebComponent;
  public view: MaintenanceWindowFormPatchSpecificsView;
  public viewConstructor: typeof MaintenanceWindowFormPatchSpecificsView;

  constructor(component: MaintenanceWindowFormPatchSpecificsWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: MaintenanceWindowFormPatchSpecificsView,
      viewTemplate: templateMaintenanceWindowFormPatchSpecifics,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$scanPatchesFormField,
        event: 'change',
        callback: this.onScanForPatchesChanged,
      },
      {
        element: this.view.dom.$applyPatchesFormField,
        event: 'change',
        callback: this.onApplyPatchesChanged,
      },
      {
        element: this.view.dom.$applyPatchesSecurityFormField,
        event: 'change',
        callback: this.onApplyPatchesTypeChanged,
      },
      {
        element: this.view.dom.$applyPatchesNonSecurityFormField,
        event: 'change',
        callback: this.onApplyPatchesTypeChanged,
      },
      {
        element: this.view.dom.$applyPatchesManuallyApprovedFormField,
        event: 'change',
        callback: this.onApplyPatchesTypeChanged,
      },
      {
        element: this.view.dom.schedulingOptions.$useSameScheduleFormField,
        event: ToggleEvents.TOGGLE_CHANGE,
        callback: this.onUseSameScheduleToggleChanged,
      },
      {
        element: this.view.dom.schedulingOptions.$useFallbackScheduleFormField,
        event: ToggleEvents.TOGGLE_CHANGE,
        callback: this.onUseFallbackScheduleToggleChanged,
      },
      {
        element: this.view.dom.additionalPatchSettings.$useVendorWebsitesFallbackFormField,
        event: ToggleEvents.TOGGLE_CHANGE,
        callback: this.onUseVendorWebsitesFallbackToggleChanged,
      },
      {
        element: this.component,
        event: 'computeFormState',
        callback: this.computeState,
      },
      {
        element: this.view.dom.additionalPatchSettings.$relayPatchCachingServers,
        event: EVENTS.CACHING_SERVERS_NUMBER_UPDATED,
        callback: this.triggerFormValidation,
      },
    ];
  }

  computeState() {
    this.computeFormComponentsVisibility();

    // binary state
    this.component.checkboxesComputationState =
      (this.view.dom.$scanPatchesFormField.Input.getFormValue() ? '1' : '0') +
      (this.view.dom.$applyPatchesFormField.Input.getFormValue() ? '1' : '0') +
      (this.view.dom.$applyPatchesSecurityFormField.Input.getFormValue() ? '1' : '0') +
      (this.view.dom.$applyPatchesNonSecurityFormField.Input.getFormValue() ? '1' : '0') +
      (this.view.dom.$applyPatchesManuallyApprovedFormField.Input.getFormValue() ? '1' : '0');
  }

  computeFormComponentsVisibility() {
    // from commonActionsOnOperationsTargeted
    const anyOperationTargetedChecked =
      this.view.dom.$scanPatchesFormField.Input.checked || this.view.dom.$applyPatchesFormField.Input.checked;
    const bothOperationTargetedChecked =
      this.view.dom.$scanPatchesFormField.Input.checked && this.view.dom.$applyPatchesFormField.Input.checked;

    toggleVisibility(this.view.dom.schedulingOptions.$useSameScheduleFormField, bothOperationTargetedChecked);
    toggleVisibility(this.view.dom.schedulingOptions.$mainContainer, anyOperationTargetedChecked);
    // SHOW/HIDE relay caching servers grid and 'Use vendor websites as fallback' toggle
    toggleVisibility(this.view.dom.additionalPatchSettings.$container, anyOperationTargetedChecked);

    // from onScanForPatchesChanged
    // SHOW schedule for scan
    toggleVisibility(
      this.view.dom.schedulers.individual.scan.$container,
      this.view.dom.$scanPatchesFormField.Input.checked
    );

    // from onApplyPatchesChanged
    // SHOW and ENABLE/HIDE and DISABLE security, non-security and manually approved checkboxes
    toggleVisibility(this.view.dom.$patchScopeLabeledContent, this.view.dom.$applyPatchesFormField.Input.checked);
    // "If missed, run as soon as possible" option, reboot options and vendor product pairs options should be visible only if security or non-security is checked
    toggleVisibility(
      this.view.dom.schedulingOptions.$ifMissedRunAsSoonAsPossibleFormField,
      this.view.dom.$applyPatchesFormField.Input.checked
    );
    toggleVisibility(this.view.dom.rebootPreferences.$container, this.view.dom.$applyPatchesFormField.Input.checked);

    // from commonActionsOnPatchScopeChanged
    const securityCheckboxValue = this.view.dom.$applyPatchesSecurityFormField.Input.checked;
    const nonSecurityCheckboxValue = this.view.dom.$applyPatchesNonSecurityFormField.Input.checked;
    const manuallyApprovedCheckboxValue = this.view.dom.$applyPatchesManuallyApprovedFormField.Input.checked;
    const anyPatchScopeChecked = securityCheckboxValue || nonSecurityCheckboxValue || manuallyApprovedCheckboxValue;

    if (!anyPatchScopeChecked) {
      toggleVisibility(this.view.dom.$patchScopeLabeledContent, false);
    }

    // from onApplyPatchesTypeChanged (security OR manually approved)
    toggleVisibility(
      this.view.dom.schedulers.individual.applySecurity.$container,
      securityCheckboxValue || manuallyApprovedCheckboxValue
    );

    // from onApplyPatchesTypeChanged (security OR non-security)
    toggleVisibility(
      this.view.dom.additionalPatchSettings.$vendorProductPairsContainer,
      securityCheckboxValue || nonSecurityCheckboxValue
    );

    // from onApplyPatchesTypeChanged (non-security)
    toggleVisibility(
      this.view.dom.schedulers.individual.applyNonSecurity.$container,
      this.view.dom.$applyPatchesNonSecurityFormField.Input.checked
    );

    // from onApplyPatchesTypeChanged (manually approved)
    // SHOW/HIDE the Manually Approved patches selector
    const manuallyApprovedFormField = this.view.dom.$manuallyApprovedPatchesSelector.controller.view.getFormField();
    const hasSelectedManuallyApprovedPatches = manuallyApprovedFormField.Input.getFormValue().length > 0;
    toggleVisibility(
      this.view.dom.$manuallyApprovedPatchesSelector,
      manuallyApprovedCheckboxValue || hasSelectedManuallyApprovedPatches
    );
  }

  onScanForPatchesChanged(ev) {
    this.commonActionsOnOperationsTargeted();

    if (ev) {
      this.computeState();
    }
  }

  onApplyPatchesChanged(ev) {
    const newValueApplyPatchesCheckbox = this.view.dom.$applyPatchesFormField.Input.checked;
    const patchScopeCheckboxes = [
      this.view.dom.$applyPatchesSecurityFormField.Input,
      this.view.dom.$applyPatchesNonSecurityFormField.Input,
      this.view.dom.$applyPatchesManuallyApprovedFormField.Input,
    ];

    this.commonActionsOnOperationsTargeted();

    for (const checkbox of patchScopeCheckboxes) {
      checkbox.stopEmitEvents = true;
      checkbox.setFormValue(newValueApplyPatchesCheckbox);
      checkbox.stopEmitEvents = false;
    }

    if (ev) {
      this.computeState();
    }
  }

  commonActionsOnOperationsTargeted() {
    const bothOperationTargetedChecked =
      this.view.dom.$scanPatchesFormField.Input.checked && this.view.dom.$applyPatchesFormField.Input.checked;

    if (!bothOperationTargetedChecked) {
      // if non or only scan or only apply patches is checked, then uncheck use same schedule option
      this.view.dom.schedulingOptions.$useSameScheduleFormField.Input.setFormValue(false);
    }
  }

  onUseSameScheduleToggleChanged(ev) {
    toggleVisibility(this.view.dom.schedulers.individual.$container, !ev.detail.checked);
    toggleVisibility(this.view.dom.schedulers.same.$container, ev.detail.checked);

    this.computeState();
  }

  onUseFallbackScheduleToggleChanged(ev) {
    // old schedulers
    toggleVisibility(this.view.dom.schedulers.same.$oldScheduler, ev.detail.checked);
    toggleVisibility(this.view.dom.schedulers.individual.scan.$oldScheduler, ev.detail.checked);
    toggleVisibility(this.view.dom.schedulers.individual.applySecurity.$oldScheduler, ev.detail.checked);
    toggleVisibility(this.view.dom.schedulers.individual.applyNonSecurity.$oldScheduler, ev.detail.checked);

    // new schedulers
    toggleVisibility(this.view.dom.schedulers.same.$newScheduler, !ev.detail.checked);
    toggleVisibility(this.view.dom.schedulers.individual.scan.$newScheduler, !ev.detail.checked);
    toggleVisibility(this.view.dom.schedulers.individual.applySecurity.$newScheduler, !ev.detail.checked);
    toggleVisibility(this.view.dom.schedulers.individual.applyNonSecurity.$newScheduler, !ev.detail.checked);

    this.computeState();
  }

  onUseVendorWebsitesFallbackToggleChanged(ev) {
    if (!ev.detail.checked) {
      // display dialog
      this.showDisableConfirmationDialog();
    } else {
      this.triggerFormValidation();
    }
  }

  triggerFormValidation() {
    this.view.formGroup.validate();
  }

  showDisableConfirmationDialog() {
    const dialogBody = document.createElement('div');
    dialogBody.innerHTML = `<p>${this.dependencies.translateFn(
      'Disabling the fallback location will stop patch download, leaving your network outdated when the provided relays are unavailable.'
    )}</p>`;

    const dialogFooter = document.createElement('div');
    dialogFooter.innerHTML = `
        <gz-button id="button-disable" type="outline" color="negative">${this.dependencies.translateFn(
          'DISABLE'
        )}</gz-button>
        <gz-button id="button-cancel" type="outline" color="secondary">${this.dependencies.translateFn(
          'CANCEL'
        )}</gz-button>
    `;

    const buttonDisable = dialogFooter.querySelector('#button-disable');
    const buttonCancel = dialogFooter.querySelector('#button-cancel');

    const dialogConfigs: GzDialog = {
      headline: this.dependencies.translateFn('Disable use of vendor websites?'),
      bodyContent: dialogBody,
      footerContent: dialogFooter,
      isOpen: true,
    };
    const dialog = GzDialogWebComponent.buildDialogElement(
      dialogConfigs,
      this.view.dom.additionalPatchSettings.$container
    );

    dialog.addEventListener(GzDialogEvents.CANCEL, this.restoreUseVendorToggleDefaultValue.bind(this));
    dialog.addEventListener(GzDialogEvents.CONFIRM, this.triggerFormValidation.bind(this));

    if (buttonCancel) {
      buttonCancel.addEventListener('click', () => {
        dialog.dismiss();
      });
    }

    if (buttonDisable) {
      buttonDisable.addEventListener('click', () => {
        dialog.close();
        dialog.emitDialogEvent(GzDialogEvents.CONFIRM);
      });
    }
  }

  restoreUseVendorToggleDefaultValue() {
    this.view.dom.additionalPatchSettings.$useVendorWebsitesFallbackFormField.Input.setFormValue(true);
    this.view.formGroup.validate();
  }

  onApplyPatchesTypeChanged(ev) {
    const anyPatchScopeChecked =
      this.view.dom.$applyPatchesSecurityFormField.Input.checked ||
      this.view.dom.$applyPatchesNonSecurityFormField.Input.checked ||
      this.view.dom.$applyPatchesManuallyApprovedFormField.Input.checked;

    if (!anyPatchScopeChecked && this.view.dom.$applyPatchesFormField.Input.getFormValue()) {
      // if (apply patches is true) and (security, non-security and manually approved are false), then apply should become false
      this.view.dom.$applyPatchesFormField.Input.setFormValue(anyPatchScopeChecked);
    }

    if (ev) {
      this.computeState();
    }
  }

  onCheckboxesComputationState(state: string) {
    switch (state) {
      case '00000':
        return this.handleFormStateScanOffApplyOffSecurityOffNonSecurityOffManuallyApprovedOff();
      case '00001':
        return this.handleFormStateScanOffApplyOffSecurityOffNonSecurityOffManuallyApprovedOn();
      case '00010':
        return this.handleFormStateScanOffApplyOffSecurityOffNonSecurityOnManuallyApprovedOff();
      case '00011':
        return this.handleFormStateScanOffApplyOffSecurityOffNonSecurityOnManuallyApprovedOn();
      case '00100':
        return this.handleFormStateScanOffApplyOffSecurityOnNonSecurityOffManuallyApprovedOff();
      case '00101':
        return this.handleFormStateScanOffApplyOffSecurityOnNonSecurityOffManuallyApprovedOn();
      case '00110':
        return this.handleFormStateScanOffApplyOffSecurityOnNonSecurityOnManuallyApprovedOff();
      case '00111':
        return this.handleFormStateScanOffApplyOffSecurityOnNonSecurityOnManuallyApprovedOn();
      case '01000':
        return this.handleFormStateScanOffApplyOnSecurityOffNonSecurityOffManuallyApprovedOff();
      case '01001':
        return this.handleFormStateScanOffApplyOnSecurityOffNonSecurityOffManuallyApprovedOn();
      case '01010':
        return this.handleFormStateScanOffApplyOnSecurityOffNonSecurityOnManuallyApprovedOff();
      case '01011':
        return this.handleFormStateScanOffApplyOnSecurityOffNonSecurityOnManuallyApprovedOn();
      case '01100':
        return this.handleFormStateScanOffApplyOnSecurityOnNonSecurityOffManuallyApprovedOff();
      case '01101':
        return this.handleFormStateScanOffApplyOnSecurityOnNonSecurityOffManuallyApprovedOn();
      case '01110':
        return this.handleFormStateScanOffApplyOnSecurityOnNonSecurityOnManuallyApprovedOff();
      case '01111':
        return this.handleFormStateScanOffApplyOnSecurityOnNonSecurityOnManuallyApprovedOn();
      case '10000':
        return this.handleFormStateScanOnApplyOffSecurityOffNonSecurityOffManuallyApprovedOff();
      case '10001':
        return this.handleFormStateScanOnApplyOffSecurityOffNonSecurityOffManuallyApprovedOn();
      case '10010':
        return this.handleFormStateScanOnApplyOffSecurityOffNonSecurityOnManuallyApprovedOff();
      case '10011':
        return this.handleFormStateScanOnApplyOffSecurityOffNonSecurityOnManuallyApprovedOn();
      case '10100':
        return this.handleFormStateScanOnApplyOffSecurityOnNonSecurityOffManuallyApprovedOff();
      case '10101':
        return this.handleFormStateScanOnApplyOffSecurityOnNonSecurityOffManuallyApprovedOn();
      case '10110':
        return this.handleFormStateScanOnApplyOffSecurityOnNonSecurityOnManuallyApprovedOff();
      case '10111':
        return this.handleFormStateScanOnApplyOffSecurityOnNonSecurityOnManuallyApprovedOn();
      case '11000':
        return this.handleFormStateScanOnApplyOnSecurityOffNonSecurityOffManuallyApprovedOff();
      case '11001':
        return this.handleFormStateScanOnApplyOnSecurityOffNonSecurityOffManuallyApprovedOn();
      case '11010':
        return this.handleFormStateScanOnApplyOnSecurityOffNonSecurityOnManuallyApprovedOff();
      case '11011':
        return this.handleFormStateScanOnApplyOnSecurityOffNonSecurityOnManuallyApprovedOn();
      case '11100':
        return this.handleFormStateScanOnApplyOnSecurityOnNonSecurityOffManuallyApprovedOff();
      case '11101':
        return this.handleFormStateScanOnApplyOnSecurityOnNonSecurityOffManuallyApprovedOn();
      case '11110':
        return this.handleFormStateScanOnApplyOnSecurityOnNonSecurityOnManuallyApprovedOff();
      case '11111':
        return this.handleFormStateScanOnApplyOnSecurityOnNonSecurityOnManuallyApprovedOn();
      default:
        break;
    }
  }

  private handleFormStateScanOffApplyOffSecurityOffNonSecurityOffManuallyApprovedOff(): void {
    // 00000: scan OFF; apply OFF; security OFF; non-security OFF; manually approved OFF
    this.setFormStateAllOff();
  }

  private handleFormStateScanOffApplyOffSecurityOffNonSecurityOffManuallyApprovedOn(): void {
    // 00001: scan OFF; apply OFF; security OFF; non-security OFF; manually approved ON

    // Invalid State. Must update checkboxes to translate to State 00000.
    this.disableFormValues(['manuallyApproved']);
  }

  private handleFormStateScanOffApplyOffSecurityOffNonSecurityOnManuallyApprovedOff(): void {
    // 00010: scan OFF; apply OFF; security OFF; non-security ON; manually approved OFF

    // Invalid State. Must update checkboxes to translate to State 00000.
    this.disableFormValues(['nonSecurity']);
  }

  private handleFormStateScanOffApplyOffSecurityOffNonSecurityOnManuallyApprovedOn(): void {
    // 00011: scan OFF; apply OFF; security OFF; non-security ON; manually approved ON

    // Invalid State. Must update checkboxes to translate to State 00000.
    this.disableFormValues(['nonSecurity', 'manuallyApproved']);
  }

  private handleFormStateScanOffApplyOffSecurityOnNonSecurityOffManuallyApprovedOff(): void {
    // 00100: scan OFF; apply OFF; security ON; non-security OFF; manually approved OFF

    // Invalid State. Must update checkboxes to translate to State 00000.
    this.disableFormValues(['security']);
  }

  private handleFormStateScanOffApplyOffSecurityOnNonSecurityOffManuallyApprovedOn(): void {
    // 00101: scan OFF; apply OFF; security ON; non-security OFF; manually approved ON

    // Invalid State. Must update checkboxes to translate to State 00000.
    this.disableFormValues(['security', 'manuallyApproved']);
  }

  private handleFormStateScanOffApplyOffSecurityOnNonSecurityOnManuallyApprovedOff(): void {
    // 00110: scan OFF; apply OFF; security ON; non-security ON; manually approved OFF

    // Invalid State. Must update checkboxes to translate to State 00000.
    this.disableFormValues(['security', 'nonSecurity']);
  }

  private handleFormStateScanOffApplyOffSecurityOnNonSecurityOnManuallyApprovedOn(): void {
    // 00111: scan OFF; apply OFF; security ON; non-security ON; manually approved ON

    // Invalid State. Must update checkboxes to translate to State 00000.
    this.disableFormValues(['security', 'nonSecurity', 'manuallyApproved']);
  }

  private handleFormStateScanOffApplyOnSecurityOffNonSecurityOffManuallyApprovedOff(): void {
    // 01000: scan OFF; apply ON; security OFF; non-security OFF; manually approved OFF

    // Invalid State. Must update checkboxes to translate to State 00000.
    this.view.dom.$applyPatchesFormField.Input.stopEmitEvents = true;
    this.disableFormValues(['apply']);
    this.view.dom.$applyPatchesFormField.Input.stopEmitEvents = false;
  }

  private handleFormStateScanOffApplyOnSecurityOffNonSecurityOffManuallyApprovedOn(): void {
    // 01001: scan OFF; apply ON; security OFF; non-security OFF; manually approved ON
    return this.setFormStateAllOff();
  }

  private handleFormStateScanOffApplyOnSecurityOffNonSecurityOnManuallyApprovedOff(): void {
    // 01010: scan OFF; apply ON; security OFF; non-security ON; manually approved OFF
    return this.setFormStateAllOff();
  }

  private handleFormStateScanOffApplyOnSecurityOffNonSecurityOnManuallyApprovedOn(): void {
    // 01011: scan OFF; apply ON; security OFF; non-security ON; manually approved ON
    return this.setFormStateAllOff();
  }

  private handleFormStateScanOffApplyOnSecurityOnNonSecurityOffManuallyApprovedOff(): void {
    // 01100: scan OFF; apply ON; security ON; non-security OFF; manually approved OFF
    return this.setFormStateAllOff();
  }

  private handleFormStateScanOffApplyOnSecurityOnNonSecurityOffManuallyApprovedOn(): void {
    // 01101: scan OFF; apply ON; security ON; non-security OFF; manually approved ON
    return this.setFormStateAllOff();
  }

  private handleFormStateScanOffApplyOnSecurityOnNonSecurityOnManuallyApprovedOff(): void {
    // 01110: scan OFF; apply ON; security ON; non-security ON; manually approved OFF
    return this.setFormStateAllOff();
  }

  private handleFormStateScanOffApplyOnSecurityOnNonSecurityOnManuallyApprovedOn(): void {
    // 01111: scan OFF; apply ON; security ON; non-security ON; manually approved ON
    return this.setFormStateAllOff();
  }

  private handleFormStateScanOnApplyOffSecurityOffNonSecurityOffManuallyApprovedOff(): void {
    // 10000: scan ON; apply OFF; security OFF; non-security OFF; manually approved: OFF
    const useOldSchedule = this.view.dom.schedulingOptions.$useFallbackScheduleFormField.Input.getFormValue();
    const config: FormEnablingConfig = this.getFormEnablingConfig(false);

    config.targetScope.apply = true;

    config.schedulingOptions.smartScan = true;
    config.schedulingOptions.useFallback = true;

    config.schedulingOptions.scanOldSchedule = useOldSchedule;
    config.schedulingOptions.scanNewSchedule = !useOldSchedule;

    config.additionalSettings.relay = true;
    config.additionalSettings.useVendorWebsites = true;

    this.setFormEnablingConfiguration(config);
  }

  private handleFormStateScanOnApplyOffSecurityOffNonSecurityOffManuallyApprovedOn(): void {
    // 10001: scan ON; apply OFF; security OFF; non-security OFF; manually approved ON

    // Invalid State. Must update checkboxes to translate to State 10000.
    this.disableFormValues(['manuallyApproved']);
  }

  private handleFormStateScanOnApplyOffSecurityOffNonSecurityOnManuallyApprovedOff(): void {
    // 10010: scan ON; apply OFF; security OFF; non-security ON; manually approved OFF

    // Invalid State. Must update checkboxes to translate to State 10000.
    this.disableFormValues(['nonSecurity']);
  }

  private handleFormStateScanOnApplyOffSecurityOffNonSecurityOnManuallyApprovedOn(): void {
    // 10011: scan ON; apply OFF; security OFF; non-security ON; manually approved ON

    // Invalid State. Must update checkboxes to translate to State 10000.
    this.disableFormValues(['nonSecurity', 'manuallyApproved']);
  }

  private handleFormStateScanOnApplyOffSecurityOnNonSecurityOffManuallyApprovedOff(): void {
    // 10100: scan ON; apply OFF; security ON; non-security OFF; manually approved OFF

    // Invalid State. Must update checkboxes to translate to State 10000.
    this.disableFormValues(['security']);
  }

  private handleFormStateScanOnApplyOffSecurityOnNonSecurityOffManuallyApprovedOn(): void {
    // 10101: scan ON; apply OFF; security ON; non-security OFF; manually approved ON

    // Invalid State. Must update checkboxes to translate to State 10000.
    this.disableFormValues(['security', 'manuallyApproved']);
  }

  private handleFormStateScanOnApplyOffSecurityOnNonSecurityOnManuallyApprovedOff(): void {
    // 10110: scan ON; apply OFF; security ON; non-security ON; manually approved OFF

    // Invalid State. Must update checkboxes to translate to State 10000.
    this.disableFormValues(['security', 'nonSecurity']);
  }

  private handleFormStateScanOnApplyOffSecurityOnNonSecurityOnManuallyApprovedOn(): void {
    // 10111: scan ON; apply OFF; security ON; non-security ON; manually approved ON

    // Invalid State. Must update checkboxes to translate to State 10000.
    this.disableFormValues(['security', 'nonSecurity', 'manuallyApproved']);
  }

  private handleFormStateScanOnApplyOnSecurityOffNonSecurityOffManuallyApprovedOff(): void {
    // 11000: scan ON; apply ON; security OFF; non-security OFF; manually approved OFF

    // Invalid State. Must update checkboxes to translate to State 10000.
    return this.disableFormValues(['apply']);
  }

  private handleFormStateScanOnApplyOnSecurityOffNonSecurityOffManuallyApprovedOn(): void {
    // 11001: scan ON; apply ON; security OFF; non-security OFF; manually approved: ON
    const scheduleConditions = this.getScheduleOptionsEnablingConditions();
    const config: FormEnablingConfig = this.getFormEnablingConfig(true);

    config.schedulingOptions.sameOldSchedule = scheduleConditions.shouldEnableSameOldSchedule;
    config.schedulingOptions.sameNewSchedule = scheduleConditions.shouldEnableSameNewSchedule;
    config.schedulingOptions.scanOldSchedule = scheduleConditions.shouldEnableOldIndividuals;
    config.schedulingOptions.scanNewSchedule = scheduleConditions.shouldEnableNewIndividuals;
    config.schedulingOptions.securityOldSchedule = scheduleConditions.shouldEnableOldIndividuals;
    config.schedulingOptions.securityNewSchedule = scheduleConditions.shouldEnableNewIndividuals;
    config.schedulingOptions.nonSecurityOldSchedule = false;
    config.schedulingOptions.nonSecurityNewSchedule = false;

    config.additionalSettings.vendorProductsRadio = false;
    config.additionalSettings.vendorProductsSelector = false;

    this.setFormEnablingConfiguration(config);
  }

  private handleFormStateScanOnApplyOnSecurityOffNonSecurityOnManuallyApprovedOff(): void {
    // 11010: scan ON; apply ON; security OFF; non-security ON; manually approved: OFF
    const scheduleConditions = this.getScheduleOptionsEnablingConditions();
    const config: FormEnablingConfig = this.getFormEnablingConfig(true);

    config.targetScope.manuallyApprovedSelector = false;

    config.schedulingOptions.sameOldSchedule = scheduleConditions.shouldEnableSameOldSchedule;
    config.schedulingOptions.sameNewSchedule = scheduleConditions.shouldEnableSameNewSchedule;
    config.schedulingOptions.scanOldSchedule = scheduleConditions.shouldEnableOldIndividuals;
    config.schedulingOptions.scanNewSchedule = scheduleConditions.shouldEnableNewIndividuals;
    config.schedulingOptions.securityOldSchedule = false;
    config.schedulingOptions.securityNewSchedule = false;
    config.schedulingOptions.nonSecurityOldSchedule = scheduleConditions.shouldEnableOldIndividuals;
    config.schedulingOptions.nonSecurityNewSchedule = scheduleConditions.shouldEnableNewIndividuals;

    this.setFormEnablingConfiguration(config);
  }

  private handleFormStateScanOnApplyOnSecurityOffNonSecurityOnManuallyApprovedOn(): void {
    // 11011: scan ON; apply ON; security OFF; non-security ON; manually approved: ON
    return this.setFormStateAllOn();
  }

  private handleFormStateScanOnApplyOnSecurityOnNonSecurityOffManuallyApprovedOff(): void {
    // 11100: scan ON; apply ON; security ON; non-security OFF; manually approved: OFF
    const scheduleConditions = this.getScheduleOptionsEnablingConditions();
    const config: FormEnablingConfig = this.getFormEnablingConfig(true);

    config.targetScope.manuallyApprovedSelector = false;

    config.schedulingOptions.sameOldSchedule = scheduleConditions.shouldEnableSameOldSchedule;
    config.schedulingOptions.sameNewSchedule = scheduleConditions.shouldEnableSameNewSchedule;
    config.schedulingOptions.scanOldSchedule = scheduleConditions.shouldEnableOldIndividuals;
    config.schedulingOptions.scanNewSchedule = scheduleConditions.shouldEnableNewIndividuals;
    config.schedulingOptions.securityOldSchedule = scheduleConditions.shouldEnableOldIndividuals;
    config.schedulingOptions.securityNewSchedule = scheduleConditions.shouldEnableNewIndividuals;
    config.schedulingOptions.nonSecurityOldSchedule = false;
    config.schedulingOptions.nonSecurityNewSchedule = false;

    this.setFormEnablingConfiguration(config);
  }

  private handleFormStateScanOnApplyOnSecurityOnNonSecurityOffManuallyApprovedOn(): void {
    // 11101: scan ON; apply ON; security ON; non-security OFF; manually approved: ON
    const scheduleConditions = this.getScheduleOptionsEnablingConditions();
    const config: FormEnablingConfig = this.getFormEnablingConfig(true);

    config.schedulingOptions.sameOldSchedule = scheduleConditions.shouldEnableSameOldSchedule;
    config.schedulingOptions.sameNewSchedule = scheduleConditions.shouldEnableSameNewSchedule;
    config.schedulingOptions.scanOldSchedule = scheduleConditions.shouldEnableOldIndividuals;
    config.schedulingOptions.scanNewSchedule = scheduleConditions.shouldEnableNewIndividuals;
    config.schedulingOptions.securityOldSchedule = scheduleConditions.shouldEnableOldIndividuals;
    config.schedulingOptions.securityNewSchedule = scheduleConditions.shouldEnableNewIndividuals;
    config.schedulingOptions.nonSecurityOldSchedule = false;
    config.schedulingOptions.nonSecurityNewSchedule = false;

    this.setFormEnablingConfiguration(config);
  }

  private handleFormStateScanOnApplyOnSecurityOnNonSecurityOnManuallyApprovedOff(): void {
    // 11110: scan ON; apply ON; security ON; non-security ON; manually approved: OFF
    const scheduleConditions = this.getScheduleOptionsEnablingConditions();
    const config: FormEnablingConfig = this.getFormEnablingConfig(true);

    config.targetScope.manuallyApprovedSelector = false;

    config.schedulingOptions.sameOldSchedule = scheduleConditions.shouldEnableSameOldSchedule;
    config.schedulingOptions.sameNewSchedule = scheduleConditions.shouldEnableSameNewSchedule;
    config.schedulingOptions.scanOldSchedule = scheduleConditions.shouldEnableOldIndividuals;
    config.schedulingOptions.scanNewSchedule = scheduleConditions.shouldEnableNewIndividuals;
    config.schedulingOptions.securityOldSchedule = scheduleConditions.shouldEnableOldIndividuals;
    config.schedulingOptions.securityNewSchedule = scheduleConditions.shouldEnableNewIndividuals;
    config.schedulingOptions.nonSecurityOldSchedule = scheduleConditions.shouldEnableOldIndividuals;
    config.schedulingOptions.nonSecurityNewSchedule = scheduleConditions.shouldEnableNewIndividuals;

    this.setFormEnablingConfiguration(config);
  }

  private handleFormStateScanOnApplyOnSecurityOnNonSecurityOnManuallyApprovedOn(): void {
    // 11111: scan ON; apply ON; security ON; non-security ON; manually approved: ON
    return this.setFormStateAllOn();
  }

  private disableFormValues(fields: ShorthandPatchScopeField[]): void {
    const updateObject: any = {};
    const map = {
      apply: 'applyPatches',
      security: 'patchScopeSecurity',
      nonSecurity: 'patchScopeNonSecurity',
      manuallyApproved: 'patchScopeManuallyApproved',
    };

    fields.forEach(field => (updateObject[map[field]] = false));

    this.view.formGroup.value = updateObject;
  }

  private setFormStateAllOff(): void {
    const config: FormEnablingConfig = this.getFormEnablingConfig(false);
    this.setFormEnablingConfiguration(config);
  }

  private setFormStateAllOn(): void {
    const scheduleConditions = this.getScheduleOptionsEnablingConditions();
    const config: FormEnablingConfig = this.getFormEnablingConfig(true);

    config.schedulingOptions.sameOldSchedule = scheduleConditions.shouldEnableSameOldSchedule;
    config.schedulingOptions.sameNewSchedule = scheduleConditions.shouldEnableSameNewSchedule;
    config.schedulingOptions.scanOldSchedule = scheduleConditions.shouldEnableOldIndividuals;
    config.schedulingOptions.scanNewSchedule = scheduleConditions.shouldEnableNewIndividuals;
    config.schedulingOptions.securityOldSchedule = scheduleConditions.shouldEnableOldIndividuals;
    config.schedulingOptions.securityNewSchedule = scheduleConditions.shouldEnableNewIndividuals;
    config.schedulingOptions.nonSecurityOldSchedule = scheduleConditions.shouldEnableOldIndividuals;
    config.schedulingOptions.nonSecurityNewSchedule = scheduleConditions.shouldEnableNewIndividuals;

    this.setFormEnablingConfiguration(config);
  }

  private setFormEnablingConfiguration(config: FormEnablingConfig): void {
    this.setSectionEnablingTargetAndScope(config.targetScope);
    this.setSectionEnablingSchedulingOptions(config.schedulingOptions);
    this.setSectionEnablingRebootPreferences(config.rebootOptions);
    this.setSectionEnablingAdditionalPatchSettings(config.additionalSettings);
  }

  private setSectionEnablingTargetAndScope(config: TargetScopeEnablingConfig): void {
    this.view.dom.$applyPatchesFormField.disabled = !config.apply;

    this.view.dom.$applyPatchesSecurityFormField.disabled = !config.security;
    this.view.dom.$applyPatchesNonSecurityFormField.disabled = !config.nonSecurity;
    this.view.dom.$applyPatchesManuallyApprovedFormField.disabled = !config.manuallyApproved;
    this.view.dom.$manuallyApprovedPatchesSelector.disabled = !config.manuallyApprovedSelector;
  }

  private setSectionEnablingSchedulingOptions(config: SchedulingOptionsEnablingConfig): void {
    // Smart scan for patches when new applications are installed
    this.view.dom.$runOnSystemChangesFormField.disabled = !config.smartScan;
    this.view.dom.schedulingOptions.$useSameScheduleFormField.disabled = !config.useSameSchedule;
    this.view.dom.schedulingOptions.$useFallbackScheduleFormField.disabled = !config.useFallback;

    this.view.dom.schedulers.same.$oldScheduler.disabled = !config.sameOldSchedule;
    this.view.dom.schedulers.same.$newScheduler.disabled = !config.sameNewSchedule;

    this.view.dom.schedulers.individual.scan.$oldScheduler.disabled = !config.scanOldSchedule;
    this.view.dom.schedulers.individual.scan.$newScheduler.disabled = !config.scanNewSchedule;

    this.view.dom.schedulers.individual.applySecurity.$oldScheduler.disabled = !config.securityOldSchedule;
    this.view.dom.schedulers.individual.applySecurity.$newScheduler.disabled = !config.securityNewSchedule;

    this.view.dom.schedulers.individual.applyNonSecurity.$oldScheduler.disabled = !config.nonSecurityOldSchedule;
    this.view.dom.schedulers.individual.applyNonSecurity.$newScheduler.disabled = !config.nonSecurityNewSchedule;

    this.view.dom.schedulingOptions.$ifMissedRunAsSoonAsPossibleFormField.disabled = !config.ifMissedRunAsap;
  }

  private setSectionEnablingRebootPreferences(rebootOptions: boolean): void {
    this.view.dom.rebootPreferences.$rebootOptions.disabled = !rebootOptions;
  }

  private setSectionEnablingAdditionalPatchSettings(config: AdditionalPatchSettingsEnablingConfig): void {
    this.view.dom.additionalPatchSettings.$relayPatchCachingServers.disabled = !config.relay;
    this.view.dom.additionalPatchSettings.$useVendorWebsitesFallbackFormField.disabled = !config.useVendorWebsites;
    this.view.dom.additionalPatchSettings.$specificVendorsProductsRadioGroupFormField.disabled =
      !config.vendorProductsRadio;
    this.view.dom.additionalPatchSettings.$vendorProductSelector.disabled = !config.vendorProductsSelector;
  }

  private getScheduleOptionsEnablingConditions() {
    const useSameSchedule = this.view.dom.schedulingOptions.$useSameScheduleFormField.Input.getFormValue();
    const useOldSchedule = this.view.dom.schedulingOptions.$useFallbackScheduleFormField.Input.getFormValue();

    return {
      shouldEnableSameOldSchedule: useSameSchedule && useOldSchedule,
      shouldEnableSameNewSchedule: useSameSchedule && !useOldSchedule,
      shouldEnableOldIndividuals: !useSameSchedule && useOldSchedule,
      shouldEnableNewIndividuals: !useSameSchedule && !useOldSchedule,
    };
  }

  private getFormEnablingConfig(enabled: boolean): FormEnablingConfig {
    return {
      targetScope: {
        apply: enabled,
        security: enabled,
        nonSecurity: enabled,
        manuallyApproved: enabled,
        manuallyApprovedSelector: enabled,
      },
      schedulingOptions: {
        smartScan: enabled,
        useSameSchedule: enabled,
        useFallback: enabled,
        sameOldSchedule: enabled,
        sameNewSchedule: enabled,
        scanOldSchedule: enabled,
        scanNewSchedule: enabled,
        securityOldSchedule: enabled,
        securityNewSchedule: enabled,
        nonSecurityOldSchedule: enabled,
        nonSecurityNewSchedule: enabled,
        ifMissedRunAsap: enabled,
      },
      rebootOptions: enabled,
      additionalSettings: {
        relay: enabled,
        useVendorWebsites: enabled,
        vendorProductsRadio: enabled,
        vendorProductsSelector: enabled,
      },
    };
  }

  async getDataForView(): Promise<{ viewData: MaintenanceWindowPatchSpecificsFormData | null; disabled: boolean }> {
    return {
      viewData: this.component.viewData,
      disabled: this.component.disabled,
    };
  }
}
